import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download, RefreshCw, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminSubscribers() {
  const { user } = useAuth();
  const [data, setData] = useState({ subscribers: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subscribers");
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (!user || user.role !== "admin") {
    return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Admin only.</div>;
  }

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/subscribers/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "alteza_subscribers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Export failed");
    }
    setDownloading(false);
  };

  const runBackfill = async () => {
    if (!window.confirm("Sync all existing sellers, buyers and subscribers into Mailchimp with their tags?")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/mailchimp/backfill");
      toast.success(`Synced to Mailchimp — ${data.sellers} sellers, ${data.buyers} buyers (${data.purchasers} purchasers), ${data.subscribers} subscribers.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed — check Mailchimp keys.");
    }
    setSyncing(false);
  };

  return (
    <div data-testid="admin-subscribers" className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Admin · Newsletter</div>
      <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Subscribers</h1>
      <p className="text-muted-foreground mt-2 text-sm">
        Everyone who signed up via the footer "Subscribe" form. Stored in your own database and synced to Mailchimp as <span className="text-foreground">Newsletter Subscriber</span>.
      </p>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <div className="border border-border p-6 rounded-sm bg-card flex-1 min-w-[200px]">
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-medium">Total subscribers</div>
          <div className="font-serif text-3xl mt-2" data-testid="subscriber-count">{data.count}</div>
        </div>
        <button
          onClick={downloadCsv}
          disabled={downloading || data.count === 0}
          data-testid="download-csv-btn"
          className="border border-border px-5 py-3 text-sm rounded-sm inline-flex items-center gap-2 hover:bg-secondary transition-colors disabled:opacity-50"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download CSV
        </button>
        <button
          onClick={runBackfill}
          disabled={syncing}
          data-testid="mailchimp-backfill-btn"
          className="btn-primary px-5 py-3 text-sm rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync existing users to Mailchimp
        </button>
        <button onClick={load} className="p-3 hover:text-primary transition-colors" title="Refresh" data-testid="refresh-subscribers">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-muted-foreground">Loading…</div>
      ) : data.subscribers.length === 0 ? (
        <div className="mt-12 text-center text-muted-foreground italic border border-dashed border-border rounded-sm py-16">
          No subscribers yet. They'll appear here when people sign up via the footer.
        </div>
      ) : (
        <div className="mt-8 border border-border rounded-sm overflow-hidden bg-card">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-border bg-secondary/50 text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            <div className="col-span-8">Email</div>
            <div className="col-span-4 text-right">Subscribed</div>
          </div>
          <div className="divide-y divide-border">
            {data.subscribers.map((s) => (
              <div key={s.email} data-testid={`subscriber-row-${s.email}`} className="grid grid-cols-12 gap-4 px-6 py-3.5 items-center hover:bg-secondary/30 transition-colors">
                <div className="col-span-8 text-sm flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-muted-foreground" /> {s.email}
                </div>
                <div className="col-span-4 text-right text-xs text-muted-foreground">
                  {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
